import { useEffect, useState, useCallback } from 'react';

const KONAMI_CODE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'KeyB',
  'KeyA',
];

/**
 * Easter egg hook that detects the Konami code sequence.
 * When activated, toggles a retro 80s neon theme.
 */
export function useKonamiCode() {
  const [isRetroMode, setIsRetroMode] = useState(false);
  const [inputSequence, setInputSequence] = useState<string[]>([]);

  const resetSequence = useCallback(() => {
    setInputSequence([]);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }

      const key = event.code;
      const newSequence = [...inputSequence, key].slice(-KONAMI_CODE.length);
      setInputSequence(newSequence);

      // Check if the sequence matches
      if (newSequence.length === KONAMI_CODE.length) {
        const isMatch = newSequence.every((k, i) => k === KONAMI_CODE[i]);
        if (isMatch) {
          setIsRetroMode((prev) => !prev);
          resetSequence();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [inputSequence, resetSequence]);

  // Apply/remove retro theme class on body
  useEffect(() => {
    if (isRetroMode) {
      document.body.classList.add('retro-mode');
      // Show a fun console message
      console.log(
        '%c🕹️ RETRO MODE ACTIVATED! 🕹️',
        'background: linear-gradient(90deg, #ff00ff, #00ffff); color: #000; font-size: 20px; padding: 10px; font-weight: bold;'
      );
    } else {
      document.body.classList.remove('retro-mode');
    }
  }, [isRetroMode]);

  return { isRetroMode, toggleRetroMode: () => setIsRetroMode((prev) => !prev) };
}
